import CustomTooltip from '../../shared/CustomTooltip';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import {
    Avatar,
    Box,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useGetIndividualClientQuery } from '../../../app/Slices/SevenDayApiSlice';
import { CustomChip } from '../../../layout/styledcomponents/CustomChip';
import GenericTable from '../../shared/GenericTable';

export default function ClientUserListComponent({
    clientName,
}: {
    clientName: string;
}) {
    const [url, setUrl] = useState('');
    const urlRef = window.location.href.split('/')[5];

    const { data: getIndividualOwnerCompany, isFetching } =
        useGetIndividualClientQuery(url);

    useEffect(() => {
        if (urlRef !== undefined) {
            setUrl(`/owners/${urlRef}/users`);
        }
    }, []);

    const theme = useTheme();

    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'firstName',
            header: 'Guest',
            size: 500,
            mantineTableBodyCellProps: {
                style: {
                    padding: 0,
                },
            },
            Cell: ({ row }) => {
                const { firstName, lastName, email } = row.original;
                return (
                    <List>
                        <ListItem sx={{ padding: '0!important' }}>
                            {!isMobile && (
                                <ListItemAvatar>
                                    <Avatar
                                        sx={{
                                            bgcolor: theme.palette.faded.main,
                                        }}
                                    >
                                        {firstName?.charAt(0)}
                                        {lastName?.charAt(0)}
                                    </Avatar>
                                </ListItemAvatar>
                            )}
                            <ListItemText
                                primary={<Typography>{firstName}</Typography>}
                                secondary={
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {email}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },
        {
            accessorKey: 'lastLoginAt',
            header: 'Last Login',
            size: isMobile ? 180 : 180,
            mantineTableBodyCellProps: {
                style: {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },
            Cell: ({ cell }) => {
                const value = cell.getValue<string | null>();
                return (
                    <List>
                        <ListItem sx={{ padding: '0!important' }}>
                            <ListItemText
                                primary={
                                    <Typography>
                                        {value
                                            ? moment(value).format('DD/MM/YYYY')
                                            : '-'}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {value
                                            ? moment(value).format('h:mm a')
                                            : ''}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
            sortingFn: 'datetime',
        },
        {
            accessorKey: 'isActive',
            header: 'Status',
            size: isMobile ? 5 : isIpad ? 50 : 130,
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (!filterValue) return true;
                return String(row.getValue('isActive')) === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Enabled', value: 'true' },
                    { label: 'Disabled', value: 'false' },
                ],
                placeholder: 'Filter by Status',
                clearable: true,
            },
            Cell: ({ cell }) => {
                const value = cell.getValue<boolean>();
                if (isMobile || isIpad) {
                    return value ? (
                        <CustomTooltip title='Enabled'>
                            <DoneIcon
                                sx={{ color: theme.palette.textAccent.main }}
                            />
                        </CustomTooltip>
                    ) : (
                        <CustomTooltip title='Disabled'>
                            <BlockIcon />
                        </CustomTooltip>
                    );
                }

                return (
                    <CustomChip
                        label={
                            <Typography
                                color={value ? 'green' : 'red'}
                                sx={{
                                    fontSize: '12px',
                                }}
                            >
                                {value ? 'Enabled' : 'Disabled'}
                            </Typography>
                        }
                        color={value ? 'green' : 'red'}
                        variant='outlined'
                    />
                );
            },
        },
    ];

    return (
        <Box sx={{ paddingTop: 3, paddingBottom: 2 }}>
            <GenericTable
                columns={columns}
                data={getIndividualOwnerCompany || []}
                tableName={`${clientName}-Users-List`}
                loading={!getIndividualOwnerCompany}
                pagination={{ pageSize: 100, pageIndex: 0 }}
                maxHeight={'600px'}
            />
        </Box>
    );
}
